import type { ComponentProps } from 'react';
import React from 'react';
import styled from '@emotion/styled';
import type { AlignItemsProps, ColorProps, DisplayProps, SpaceProps } from 'styled-system';
import { alignItems, color, display, space, themeGet } from 'styled-system';

import { ButtonBase } from 'common/components/Button';
import { BodyRegularBR1 } from 'common/components/Typography';
import { useRovingTabIndex } from 'common/hooks/useRovingTabIndex';
import { useLocalization } from 'common/providers/LocaleProvider';
import { openSideNav } from 'common/utils/navigation';
import { trackNavigationLinkClick } from './tracking';

const Container = styled.div<DisplayProps & SpaceProps & AlignItemsProps>`
  ${display};
  ${space};
  ${alignItems};
`;

const StyledLinkButton = styled(ButtonBase)<ComponentProps<typeof ButtonBase> & ColorProps>`
  ${color};

  ${props => ({
    '&:hover': {
      textDecorationLine: 'underline',
      textDecorationColor: themeGet('colors.sys.primary.border.default')(props),
      textDecorationThickness: themeGet('radii.small')(props),
      textUnderlineOffset: themeGet('space.sp_12')(props),
    },
  })};
`;

const containerStyles: ComponentProps<typeof Container> = {
  display: ['none', 'none', 'flex'],
  ml: '36px',
  mr: 'sp_16',
  alignItems: 'center',
};

const linkButtonStyles: Partial<ComponentProps<typeof StyledLinkButton>> = {
  py: 'sp_8',
  px: 'sp_12',
  color: 'sys.neutral.text.strong',
};

interface NavigationLinksProps {
  tabLabels: string[];
}

const NavigationLinks = ({ tabLabels }: NavigationLinksProps) => {
  const { t } = useLocalization();
  const { setFocusedIndex, getTabIndex, getRef, handleKeyDown } = useRovingTabIndex({
    itemCount: tabLabels.length,
  });

  if (!tabLabels.length) return null;

  return (
    <Container
      data-testid="navigation-links"
      role="toolbar"
      aria-label={t('h24_navigation_links_aria_label')}
      {...containerStyles}
      onKeyDown={handleKeyDown}
    >
      {tabLabels.map((label, index) => (
        <StyledLinkButton
          key={`${label}-${index}`}
          aria-haspopup="dialog"
          tabIndex={getTabIndex(index)}
          ref={getRef(index)}
          {...linkButtonStyles}
          onClick={() => {
            setFocusedIndex(index);
            trackNavigationLinkClick(label, index, tabLabels.length);
            openSideNav({ activeTab: label });
          }}
        >
          <BodyRegularBR1 color="inherit">{label}</BodyRegularBR1>
        </StyledLinkButton>
      ))}
    </Container>
  );
};

export default NavigationLinks;
